/**
 * Quando o piso VO pode sair da Group Room Twilio sem mudar a experiência.
 *
 * Twilio cobra participant-minute mesmo sem câmera/microfone.
 * Presença, ponto e assentos ficam na API; A/V só volta quando alguém
 * liga mídia ou o piso recebe um wake.
 */

export const VO_TWILIO_SOLO_GRACE_MS = 8000;
export const VO_TWILIO_SILENT_GRACE_MS = 20000;
export const VO_TWILIO_LIVE_PEER_WAKE_MS = 25000;
export const VO_PRESENCE_HEARTBEAT_MS = 30000;
export const VO_PRESENCE_POLL_HELD_MS = 3000;
export const VO_PRESENCE_POLL_LIVE_MS = 8000;

/** pagehide dispara ao trocar de aba no Safari/Chrome; isso não é saída do piso. */
export function shouldKeepVoOfficeOnPageHide({ isVirtualOffice = false } = {}) {
  return Boolean(isVirtualOffice);
}

const AWAY_STATUSES = new Set(["away", "offline", "in_other_meeting"]);

function huddleVideoIdFromPerson(person) {
  const ref = person?.otherMeetingRef;
  const id = Number(ref?.videoRoomId || ref?.video_room_id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

/** Fora do tapete: em sala interna, reunião ou ausente. */
export function isOffVoFloorRug(person) {
  const status = String(person?.status || "present").toLowerCase();
  if (AWAY_STATUSES.has(status)) return true;
  return huddleVideoIdFromPerson(person) > 0;
}

export function userIdsOffVoFloorRug(people = []) {
  return new Set(
    (Array.isArray(people) ? people : [])
      .filter(isOffVoFloorRug)
      .map((row) => Number(row.userId))
      .filter((id) => Number.isFinite(id) && id > 0)
  );
}

function sameVoPerson(person, { selfUserId, selfName, selfIdentity } = {}) {
  const uid = Number(person?.userId);
  const selfId = Number(selfUserId);
  if (Number.isFinite(uid) && uid > 0 && uid === selfId) return true;
  const identity = String(person?.twilioIdentity || person?.identity || "").trim();
  const mine = String(selfIdentity || "").trim();
  if (identity && mine && identity === mine) return true;
  const name = String(person?.userName || "").trim().toLowerCase();
  const self = String(selfName || "").trim().toLowerCase();
  if (name && self && name === self) return true;
  return false;
}

export function isVoPresencePeer(person, selfUserId, extra = {}) {
  if (sameVoPerson(person, { selfUserId, ...extra })) return false;
  const uid = Number(person?.userId);
  if (!Number.isFinite(uid) || uid <= 0) return false;
  const status = String(person?.status || "present").toLowerCase();
  if (AWAY_STATUSES.has(status)) return false;
  if (huddleVideoIdFromPerson(person) > 0) return false;
  return true;
}

export function listVoPresencePeers(people, selfUserId, extra = {}) {
  return (Array.isArray(people) ? people : []).filter((row) =>
    isVoPresencePeer(row, selfUserId, extra)
  );
}

/** Colegas no piso que ainda não têm tile Twilio (cam/mic off / park). */
export function listVoPresenceGhosts(people, selfUserId, extra = {}) {
  const remoteIds = new Set(
    [...(extra.remoteUserIds || [])].map(Number).filter((n) => Number.isFinite(n) && n > 0)
  );
  const remoteIdentities = new Set(
    [...(extra.remoteIdentities || [])].map((s) => String(s || "").trim()).filter(Boolean)
  );
  return listVoPresencePeers(people, selfUserId, extra).filter((row) => {
    if (remoteIds.has(Number(row.userId))) return false;
    const ident = String(row.twilioIdentity || row.identity || "").trim();
    if (ident && remoteIdentities.has(ident)) return false;
    return true;
  });
}

export function countVoPresencePeers(people, selfUserId) {
  return listVoPresencePeers(people, selfUserId).length;
}

/** Colegas já na Group Room — quem entra mudo ainda precisa conectar. */
export function isVoLiveVideoPeer(person) {
  if (!person) return false;
  return Boolean(person.fromVideo || person.from_video);
}

export function countVoLiveVideoPeers(people, selfUserId, extra = {}) {
  return listVoPresencePeers(people, selfUserId, extra).filter(isVoLiveVideoPeer).length;
}

export function liveTwilioPeerCount(participantsInCall, selfIdentity) {
  const self = String(selfIdentity || "").trim();
  return (Array.isArray(participantsInCall) ? participantsInCall : []).filter((row) => {
    const identity = String(row?.identity || "").trim();
    if (!identity) return false;
    if (self && identity === self) return false;
    return true;
  }).length;
}

export function anyoneWantsVoMedia({
  localCamOn = false,
  localMicOn = false,
  sharingScreen = false,
  remoteWantsMedia = false,
} = {}) {
  return Boolean(localCamOn || localMicOn || sharingScreen || remoteWantsMedia);
}

/**
 * Já conectado: mantém a sala se alguém precisa de A/V.
 * Silêncio com colegas (ou sozinho) libera o Twilio após o grace do efeito.
 */
export function shouldKeepVoTwilioLive({
  isVoFloor = false,
  pausedForOtherMeeting = false,
  sharingScreen = false,
  localCamOn = false,
  localMicOn = false,
  remoteWantsMedia = false,
} = {}) {
  if (!isVoFloor) return true;
  if (pausedForOtherMeeting) return false;
  return anyoneWantsVoMedia({
    localCamOn,
    localMicOn,
    sharingScreen,
    remoteWantsMedia,
  });
}

/**
 * Parqueado: só reentra se alguém pediu mídia ou o piso recebeu wake.
 * Colega mudo ainda na Group Room não conta — isso ciclava connect/disconnect ~40s.
 */
export function shouldJoinVoTwilio({
  isVoFloor = false,
  pausedForOtherMeeting = false,
  sharingScreen = false,
  localCamOn = false,
  localMicOn = false,
  remoteWantsMedia = false,
  wakeRequested = false,
} = {}) {
  if (!isVoFloor || pausedForOtherMeeting) return false;
  if (
    anyoneWantsVoMedia({
      localCamOn,
      localMicOn,
      sharingScreen,
      remoteWantsMedia,
    })
  ) {
    return true;
  }
  return Boolean(wakeRequested);
}

export function voTwilioHoldGraceMs({
  keepLive = false,
  presencePeerCount = 0,
  remoteTwilioCount = 0,
} = {}) {
  if (keepLive) return 0;
  const peers = Math.max(Number(presencePeerCount) || 0, Number(remoteTwilioCount) || 0);
  return peers > 0 ? VO_TWILIO_SILENT_GRACE_MS : VO_TWILIO_SOLO_GRACE_MS;
}

export function shouldSkipVoTwilioOnJoin({
  isVoFloor = false,
  requireCamera = false,
  localCamOn = false,
  localMicOn = false,
  liveVideoPeerCount = 0,
} = {}) {
  if (!isVoFloor || requireCamera) return false;
  if (localCamOn || localMicOn) return false;
  if (Number(liveVideoPeerCount) > 0) return false;
  return true;
}

export function voPresencePollMs(held) {
  return held ? VO_PRESENCE_POLL_HELD_MS : VO_PRESENCE_POLL_LIVE_MS;
}
